import React, { useEffect, useMemo, useRef, useState } from "react";
import { Images } from "@/constants/images";
import { sortASC, sortDESC, STATUS_FORMAT_COLOR, StatusId, stringEmDash, TIME_FORMAT } from "@/constants/app_constants";
import {
  DELIVERED,
  DELIVERED_DATE,
  DELIVERY_CONFIRMED,
  DOWNLOAD,
  DRAWING,
  DUE_DATE,
  EXPORT_CSV,
  IN_PRODUCTION,
  JOB_CODE,
  JOB_ID,
  ORDER_BY,
  ORDER_DATE,
  PICK_UP_DELIVERY,
  PO_NO,
  PROJECT_ADDRESS,
  PROJECT_ID,
  PROJECT_NAME,
  READY,
  READY_DATE,
  SITE_SKETCHES_CONFIG_TYPE,
  SKETCH_NO,
  SOMETHING_WENT_WRONG,
  STATUS,
  TEXT_COMPANY,
  TEXT_FILTERS,
  TOTAL_SKETCH,
  TOTAL_SKETCHES
} from "@/constants/strings";
import { useSketchStore } from "@/stores/useSketchStore";
import { useDateRangeStoreSiteSketch } from "@/hooks/useDateRangeStore";
import { useFilterStore } from "@/stores/useFilterStore";
import { useToast } from "@/components/common/toast/ToastProvider.tsx";
import { Filter } from "./Filter";
import { ToolBox } from "./ToolBox/ToolBox";
import { useMutation } from "@tanstack/react-query";
import { siteSkectchFilter, siteSkectchNumberStatus, siteSketchesService, siteSketchUiConfig } from "@/services/siteSketchesService";
import { ToastType } from "@/components/common/toast/Toast";
import { ChevronDown, ChevronUp } from "lucide-react";
import dayjs from "dayjs";
import { InputAndDateFilter, ISiteSketchesList, ISiteSketchesResponse } from "@/types/SitesSketches";
import { Button, Tag } from "antd";
import { useExportCsv } from "@/hooks/useCSVExport.ts";
import { THEME } from "@/constants";
import pathnames from "@/pathnames";
import BaseTable from "@/components/table-ant/table";
import { createOnCellFunction, RenderWithFallback } from "@/components/table-ant/render-table-common";
import { ColumnType } from "antd/es/table";
import { SortIcon } from "@/components/common/SortIcon";
import { formatTimeDayMonthYear, formatTimeDueDate, formatTimeHoursDayMonthYear } from "@/components/table-ant/table-common";
import { ErrorResponse } from "@/services/api";
import { UIConfigModal } from "@/components/UIConfigModal";
import { useShallow } from "zustand/shallow";

export const SiteSketches: React.FC = () => {
  type StatusCount = {
    status: string;
    count: number;
    icon: string;
    icColor: string;
  };
  const [isVisible, setIsVisible] = useState(true);
  const { showToast } = useToast();
  const [showUIConfigModal, setShowUIConfigModal] = useState(false);
  const [statusCounts, setStatusCounts] = useState<StatusCount[]>([]);

  const fetchedPages = useRef<Set<number>>(new Set());
  const attemptedPages = useRef<Set<number>>(new Set());

  const filters = useFilterStore(state => state.filters);
  const selected = useFilterStore(state => state.selected);
  const setOptions = useFilterStore(state => state.setOptions);

  const isFetchingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const shouldStopRef = useRef(false);

  const { startDate, endDate } = useDateRangeStoreSiteSketch();
  const fromDate = startDate?.format(TIME_FORMAT.DATE) || dayjs().format(TIME_FORMAT.DATE);
  const toDate = endDate?.format(TIME_FORMAT.DATE) || dayjs().format(TIME_FORMAT.DATE);

  const [filterSelected, setFilterSelected] = useState<InputAndDateFilter>();
  const [appliedFilters, setAppliedFilters] = useState<typeof selected>({});
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const pageTitle = pathnames.siteSketches.main.name;
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [totalJobs, setTotalJobs] = useState(0);

  // Company
  const companyOptions = filters["companies"] || [];
  const selectedCompanies = appliedFilters["companies"] || [];
  const selectedCompanyIds = selectedCompanies.map(c => c.id);
  const selectedAll = selectedCompanyIds.length === companyOptions.length;
  // ProjectNames
  const projectNamesOptions = filters["projectNames"] || [];
  const selectedProjectNames = appliedFilters["projectNames"] || [];
  const selectedProjectNamesArray = selectedProjectNames.map(c => c.displayName);
  const selectedProjectNamesAll = selectedProjectNamesArray.length === projectNamesOptions.length;
  // OrderBy
  const orderByOptions = filters["orderBy"] || [];
  const selectedOrderBy = appliedFilters["orderBy"] || [];
  const selectedOrderByIds = selectedOrderBy.map(c => c.id);
  const selectedOrderByAll = selectedOrderByIds.length === orderByOptions.length;
  // JobCodes
  const jobCodesOptions = filters["jobCodes"] || [];
  const selectedJobCodes = appliedFilters["jobCodes"] || [];
  const selectedJobCodesIds = selectedJobCodes.map(c => c.id);
  const selectedJobCodesAll = selectedJobCodesIds.length === jobCodesOptions.length;
  // Status
  const statusOptions = filters["statuses"] || [];
  const selectedStatus = appliedFilters["statuses"] || [];
  const selectedStatusIds = selectedStatus.map(c => c.id);
  const selectedStatusAll = selectedStatusIds.length === statusOptions.length;
  // States
  const statesOptions = filters["states"] || [];
  const selectedStates = appliedFilters["states"] || [];
  const selectedStatesIds = selectedStates.map(c => c.id);
  const selectedStatesAll = selectedStatesIds.length === statesOptions.length;

  const memoizedExportCsvFilters = useMemo(() => filterSelected!, [filterSelected!]);
  const pathname = location.pathname === "/" ? "site-sketch" : location.pathname;
  const CSV_FILENAME = "site_sketches";
  const mutation = useExportCsv(CSV_FILENAME, pathname, memoizedExportCsvFilters);

  const initialPayload = {
    page: 1,
    pageSize: 15,
    fromDate,
    toDate
  };

  const {
    sortModel,
    setSortModel,
    siteSketchList,
    setSiteSketchList,
    page,
    pageSize,
    setPage,
    setPageSize,
    search_text,
    setSearchText,
    uiConfig,
    setUIConfig,
    order_by,
    setOrderBy
  } = useSketchStore(
    useShallow(state => ({
      sortModel: state.sortModel,
      setSortModel: state.setSortModel,
      siteSketchList: state.siteSketchList,
      setSiteSketchList: state.setSiteSketchList,
      page: state.page,
      pageSize: state.pageSize,
      setPage: state.setPage,
      setPageSize: state.setPageSize,
      search_text: state.search_text,
      setSearchText: state.setSearchText,
      uiConfig: state.uiConfig,
      setUIConfig: state.setUIConfig,
      order_by: state.order_by,
      setOrderBy: state.setOrderBy
    }))
  );
  const getSortDirection = (name: string) => (sortModel.columnKey === name ? (sortModel.order === sortASC ? "ascend" : "descend") : null);

  const addIconColor = (items: any) => {
    const newItems = items.map((item: any) => {
      switch (item.status) {
        case StatusId.inProduction:
          item.status = IN_PRODUCTION;
          break;
        case StatusId.completed:
          item.status = DELIVERED;
          break;
        default:
          break;
      }
      return {
        ...item,
        icColor: THEME.COLORS.SITES_SKETCHES[item.status?.replace(/\s+/g, "").toUpperCase() as keyof typeof THEME.COLORS.SITES_SKETCHES],
        icon: Images[`Icon${item.status.replace(/\s+/g, "")}` as keyof typeof Images]
      };
    });
    setStatusCounts(newItems);
  };

  const getSketch = useMutation({
    mutationFn: siteSkectchNumberStatus.getNumberStatus,
    onSuccess: (data: any) => {
      addIconColor(data.items);
    },
    onError: (err: ErrorResponse) => {
      showToast(ToastType.error, err.message);
    }
  });

  // #region Filters
  const requestPayload = {
    fromDate,
    toDate,
    search_text: search_text || undefined,
    page: page,
    pageSize: 20,
    companyId__in: selectedAll ? undefined : selectedCompanyIds,
    sketchProjectName__in: selectedProjectNamesAll ? undefined : selectedProjectNamesArray,
    orderBy__in: selectedOrderByAll ? undefined : selectedOrderByIds,
    sketchJobCode__in: selectedJobCodesAll ? undefined : selectedJobCodesIds,
    status__in: selectedStatusAll ? undefined : selectedStatusIds,
    state__in: selectedStatesAll ? undefined : selectedStatesIds,
    order_by: order_by
  };

  const getSiteSketchFilter = useMutation({
    mutationFn: siteSkectchFilter.getSiteSkectchFilter,
    onSuccess: (data: any) => {
      const dataResponse = data;
      useFilterStore.getState().clearFilter();

      // Store all project names
      useFilterStore.getState().setAllProjectNames(dataResponse?.projectNames);

      Object.entries(dataResponse).forEach(([key, options]) => {
        setOptions(
          key,
          (options as any[]).map((c: any) => {
            if (key === "projectNames") {
              return {
                id: c.id,
                displayName: c.displayName,
                companyIds: c.companyIds
              };
            } else {
              return {
                id: c.id,
                displayName: c.displayName
              };
            }
          })
        );
      });
    },
    onError: (err: ErrorResponse) => {
      showToast(ToastType.error, err.message);
    }
  });

  const handleFilterData = (filterType?: string) => {
    useSketchStore.getState().reset();
    attemptedPages.current.clear();
    fetchedPages.current.clear();
    hasMoreRef.current = true;
    isFetchingRef.current = false;
    shouldStopRef.current = false;

    if (filterType === "filter") {
      setAppliedFilters({ ...selected });
      setIsFilterApplied(true);
    } else {
      setAppliedFilters({});
      getSiteSketchFilter.mutate({ fromDate, toDate });
      setSearchText("");
      setIsFilterApplied(false);
      requestPayload.page = 1;
      requestPayload.pageSize = 20;
      fetchingSketchData(requestPayload);
    }
  };
  // #endregion

  // #region UI Config
  // Fetch UI Config
  const getSketchUI = useMutation({
    mutationFn: siteSketchUiConfig.getSiteSketchUiConfig,
    onSuccess: (data: any) => {
      const columnsConfig = data.columns;
      setUIConfig(columnsConfig);
    },
    onError: _ => {
      showToast(ToastType.error, SOMETHING_WENT_WRONG);
    }
  });

  // Update UI Config
  const updateSketchesUIConfig = useMutation({
    mutationFn: siteSketchUiConfig.updateUiConfig,
    onSuccess: (data: any) => {
      setUIConfig(data.columns);
    },
    onError: _ => {
      showToast(ToastType.error, SOMETHING_WENT_WRONG);
    }
  });

  const handleSaveUIConfig = (selectedColumns: ColumnType<ISiteSketchesList>[]) => {
    updateSketchesUIConfig.mutate({
      configType: SITE_SKETCHES_CONFIG_TYPE,
      columns: selectedColumns.map(col => String(col.key!)).filter(Boolean)
    });
    setShowUIConfigModal(false);
  };
  // #endregion

  // #region Fetch Data
  const getSketchData = useMutation({
    mutationFn: siteSketchesService.getSketchesList,
    onSuccess: (data: ISiteSketchesResponse) => {
      if (data.meta.page === 1 || data.meta.page === null) {
        setSiteSketchList(data.items);
      } else {
        setSiteSketchList((prevData: ISiteSketchesList[]) => {
          const existingIds = new Set(prevData.map(item => item.id));
          const newItems = data.items.filter(item => !existingIds.has(item.id));
          return [...prevData, ...newItems];
        });
      }
      setPage(data.meta.page + 1);
      setHasNextPage(data.meta.hasNextPage);
      setTotalJobs(data.meta.totalRecords);
      setPageSize(data.meta.pageSize);
    },
    onError: _ => {
      showToast(ToastType.error, SOMETHING_WENT_WRONG);
    }
  });
  // #endregion

  const fetchingSketchData = ({
    fromDate,
    toDate,
    search_text,
    page: page,
    selectedCompany,
    selectedProjectNames,
    selectedOrderByIds,
    selectedJobCodesIds,
    selectedStatusIds,
    selectedStatesIds,
    order_by
  }: any = {}) => {
    const filtersSelected = {
      fromDate,
      toDate,
      search_text,
      page: isFilterApplied ? page : undefined,
      companyId__in: selectedCompany,
      sketchProjectName__in: selectedProjectNames,
      orderBy__in: selectedOrderByIds,
      sketchJobCode__in: selectedJobCodesIds,
      status__in: selectedStatusIds,
      state__in: selectedStatesIds,
      order_by
    };

    getSketchData.mutate({
      fromDate,
      toDate,
      search_text,
      page: page,
      pageSize,
      companyId__in: selectedCompany,
      sketchProjectName__in: selectedProjectNames,
      orderBy__in: selectedOrderByIds,
      sketchJobCode__in: selectedJobCodesIds,
      status__in: selectedStatusIds,
      state__in: selectedStatesIds,
      order_by: order_by
    });
    setFilterSelected(filtersSelected);
  };

  useEffect(() => {
    if (Object.keys(appliedFilters).length === 0) return;
    fetchingSketchData({
      fromDate,
      toDate,
      search_text: search_text || undefined,
      page: 1,
      selectedCompany: selectedAll ? undefined : selectedCompanyIds,
      selectedProjectNames: selectedProjectNamesAll ? undefined : selectedProjectNamesArray,
      selectedOrderByIds: selectedOrderByAll ? undefined : selectedOrderByIds,
      selectedJobCodesIds: selectedJobCodesAll ? undefined : selectedJobCodesIds,
      selectedStatusIds: selectedStatusAll ? undefined : selectedStatusIds,
      selectedStatesIds: selectedStatesAll ? undefined : selectedStatesIds,
      order_by: requestPayload.order_by
    });
  }, [appliedFilters, order_by, isFilterApplied]);

  // #region Columns
  const columns: ColumnType<ISiteSketchesList>[] = useMemo(
    () => [
      {
        dataIndex: "sketchJobId",
        key: "sketchJobId",
        title: JOB_ID,
        width: 120,
        onCell: createOnCellFunction(120),
        sorter: true,
        sortIcon: ({ sortOrder }) => <SortIcon sortOrder={sortOrder} />,
        render: item => <RenderWithFallback data={item} />,
        sortOrder: getSortDirection("sketchJobId")
      },
      {
        dataIndex: "sketchJobCode",
        key: "sketchJobCode",
        title: JOB_CODE,
        width: 140,
        onCell: createOnCellFunction(140),
        sorter: true,
        sortIcon: ({ sortOrder }) => <SortIcon sortOrder={sortOrder} />,
        render: item => <RenderWithFallback data={item} />,
        sortOrder: getSortDirection("sketchJobCode")
      },
      {
        dataIndex: "sketchNumber",
        key: "sketchNumber",
        title: SKETCH_NO,
        width: 140,
        onCell: createOnCellFunction(140),
        sorter: true,
        sortIcon: ({ sortOrder }) => <SortIcon sortOrder={sortOrder} />,
        render: item => <RenderWithFallback data={item} />,
        sortOrder: getSortDirection("sketchNumber")
      },
      {
        dataIndex: "companyName",
        key: "company",
        title: TEXT_COMPANY,
        width: 200,
        onCell: createOnCellFunction(200),
        sorter: true,
        showSorterTooltip: false,
        sortIcon: ({ sortOrder }) => <SortIcon sortOrder={sortOrder} />,
        render: item => <RenderWithFallback data={item || stringEmDash} />,
        sortOrder: getSortDirection("company")
      },
      {
        dataIndex: "sketchProjectName",
        key: "sketchProjectName",
        title: PROJECT_NAME,
        width: 200,
        onCell: createOnCellFunction(200),
        sorter: true,
        showSorterTooltip: false,
        sortIcon: ({ sortOrder }) => <SortIcon sortOrder={sortOrder} />,
        render: item => <RenderWithFallback data={item} />,
        sortOrder: getSortDirection("sketchProjectName")
      },
      {
        dataIndex: "sketchJobAddress",
        key: "sketchJobAddress",
        title: PROJECT_ADDRESS,
        width: 200,
        onCell: createOnCellFunction(200),
        sorter: true,
        showSorterTooltip: false,
        sortIcon: ({ sortOrder }) => <SortIcon sortOrder={sortOrder} />,
        render: item => <RenderWithFallback data={item} />,
        sortOrder: getSortDirection("sketchJobAddress")
      },
      {
        dataIndex: "sketchProjectId",
        key: "sketchProjectId",
        title: PROJECT_ID,
        width: 140,
        onCell: createOnCellFunction(140),
        sorter: true,
        showSorterTooltip: false,
        sortIcon: ({ sortOrder }) => <SortIcon sortOrder={sortOrder} />,
        render: item => <RenderWithFallback data={item} />,
        sortOrder: getSortDirection("sketchProjectId")
      },
      {
        dataIndex: "poNumber",
        key: "poNumber",
        title: PO_NO,
        width: 120,
        onCell: createOnCellFunction(120),
        sorter: true,
        showSorterTooltip: false,
        sortIcon: ({ sortOrder }) => <SortIcon sortOrder={sortOrder} />,
        render: item => <RenderWithFallback data={item} />,
        sortOrder: getSortDirection("poNumber")
      },
      {
        dataIndex: "orderDate",
        key: "orderDate",
        title: ORDER_DATE,
        width: 140,
        onCell: createOnCellFunction(140),
        sorter: true,
        showSorterTooltip: false,
        sortIcon: ({ sortOrder }) => <SortIcon sortOrder={sortOrder} />,
        sortOrder: getSortDirection("orderDate"),
        render: item => <RenderWithFallback data={formatTimeDayMonthYear(item)} />,
        defaultSortOrder: "descend"
      },
      {
        dataIndex: "dueDate",
        key: "dueDate",
        title: DUE_DATE,
        width: 130,
        onCell: createOnCellFunction(130),
        sorter: true,
        showSorterTooltip: false,
        sortIcon: ({ sortOrder }) => <SortIcon sortOrder={sortOrder} />,
        render: item => <RenderWithFallback data={formatTimeDueDate(item)} />,
        sortOrder: getSortDirection("dueDate")
      },
      {
        dataIndex: "status",
        key: "status",
        title: STATUS,
        width: 130,
        onCell: createOnCellFunction(130),
        sorter: true,
        showSorterTooltip: false,
        sortIcon: ({ sortOrder }) => <SortIcon sortOrder={sortOrder} />,
        render: (status: string) => {
          const color = STATUS_FORMAT_COLOR[status] || STATUS_FORMAT_COLOR["default"];
          return (
            <Tag
              color={color}
              style={{
                color: "white",
                borderRadius: 20,
                padding: "0 12px",
                fontWeight: 500,
                fontSize: 12
              }}
            >
              {status}
            </Tag>
          );
        },
        sortOrder: getSortDirection("status")
      },
      {
        dataIndex: "state",
        key: "state",
        title: PICK_UP_DELIVERY,
        width: 180,
        onCell: createOnCellFunction(180),
        sorter: true,
        showSorterTooltip: false,
        sortIcon: ({ sortOrder }) => <SortIcon sortOrder={sortOrder} />,
        render: item => <RenderWithFallback data={item} />,
        sortOrder: getSortDirection("state")
      },
      {
        dataIndex: "confirmDelivery",
        key: "confirmDelivery",
        title: DELIVERY_CONFIRMED,
        width: 200,
        onCell: createOnCellFunction(200),
        sorter: true,
        showSorterTooltip: false,
        sortIcon: ({ sortOrder }) => <SortIcon sortOrder={sortOrder} />,
        render: item => <RenderWithFallback data={item} />,
        sortOrder: getSortDirection("confirmDelivery")
      },
      {
        dataIndex: "qcDate",
        key: "qcDate",
        title: READY_DATE,
        width: 150,
        onCell: createOnCellFunction(150),
        sorter: true,
        showSorterTooltip: false,
        sortIcon: ({ sortOrder }) => <SortIcon sortOrder={sortOrder} />,
        render: item => <RenderWithFallback data={formatTimeHoursDayMonthYear(item)} />,
        sortOrder: getSortDirection("qcDate")
      },
      {
        dataIndex: "completedDate",
        key: "completedDate",
        title: DELIVERED_DATE,
        width: 160,
        onCell: createOnCellFunction(160),
        sorter: true,
        showSorterTooltip: false,
        sortIcon: ({ sortOrder }) => <SortIcon sortOrder={sortOrder} />,
        render: item => <RenderWithFallback data={formatTimeHoursDayMonthYear(item)} />,
        sortOrder: getSortDirection("completedDate")
      },
      {
        dataIndex: "drawing",
        key: "drawing",
        title: DRAWING,
        width: 130,
        onCell: createOnCellFunction(130),
        sorter: true,
        showSorterTooltip: false,
        sortIcon: ({ sortOrder }) => <SortIcon sortOrder={sortOrder} />,
        render: (item: ISiteSketchesList) => {
          const label = item.drawing || stringEmDash;
          const link = item.drawingLink;
          if (!label || !link) {
            return stringEmDash;
          }
          return (
            <a href={link} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700">
              {DOWNLOAD}
            </a>
          );
        },
        sortOrder: getSortDirection("drawing")
      },
      {
        dataIndex: "orderBy",
        key: "orderBy",
        title: ORDER_BY,
        width: 130,
        onCell: createOnCellFunction(130),
        sorter: true,
        showSorterTooltip: false,
        sortIcon: ({ sortOrder }) => <SortIcon sortOrder={sortOrder} />,
        render: item => <RenderWithFallback data={item} />,
        sortOrder: getSortDirection("orderBy")
      }
    ],
    [STATUS_FORMAT_COLOR, createOnCellFunction, sortModel]
  );

  const visibleColumns = useMemo(() => {
    const selectedMap = new Map(uiConfig.filter(c => c.selected).map(c => [c.name, c]));
    const filtered = columns
      .filter(col => col.key !== undefined && selectedMap.has(String(col.key)))
      .sort((a, b) => {
        const orderA = selectedMap.get(String(a.key))?.order ?? 0;
        const orderB = selectedMap.get(String(b.key))?.order ?? 0;
        return orderA - orderB;
      });
    return filtered;
  }, [columns, uiConfig]);
  // #endregion Columns

  // #region Initial data
  useEffect(() => {
    setSortModel({ columnKey: "orderDate", order: "DESC" });
    setSearchText("");
    requestPayload.page = 1;
    requestPayload.pageSize = 15;
    getSketchData.mutate(initialPayload);
    getSketch.mutate();
    getSiteSketchFilter.mutate({ fromDate, toDate });
    getSketchUI.mutate();
  }, []);
  // #endregion

  // #region Lazy Loading
  const handleLoadMore = () => {
    if (!hasNextPage || getSketchData.isPending) return;
    const nextPagePayload = {
      page: requestPayload.page,
      pageSize: 20,
      order_by: requestPayload.order_by
    };
    fetchingSketchData(nextPagePayload);
  };
  // #endregion Lazy Loading

  // #region Sort
  const handleCreateSorter = (_pagination: any, _filters: any, sorter: any) => {
    const { field, order } = sorter;
    let sortDirection = sortModel.order === sortASC ? sortDESC : sortASC;
    let sortBy = { [sortModel.columnKey]: sortDirection };

    if (field && order) {
      sortDirection = order === "ascend" ? sortASC : sortDESC;
      sortBy = { [field === "companyName" ? "company" : field]: sortDirection };
    }
    setSortModel({
      order: sortDirection,
      columnKey: sorter.columnKey ?? sortModel.columnKey
    });

    setPageSize(20);
    setOrderBy(Object.keys(sortBy).length > 0 ? JSON.stringify(sortBy) : JSON.stringify({ [sortModel.columnKey]: sortModel.order }));
    requestPayload.page = 1;
    requestPayload.order_by = order_by;
    fetchingSketchData(requestPayload);
  };
  // #endregion Sort

  const Dashboard = () => {
    const statusOrder = [IN_PRODUCTION, READY, DELIVERED];
    const sortedStatusCounts = [...statusCounts].sort((a, b) => statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status));
    return (
      <div className="w-full h-full justify-start ">
        <div className="text-xl font-semibold leading-loose">{pageTitle}</div>
        <div className="dashboard h-[70px] w-full inline-flex gap-3">
          {/* Render Dashboard */}
          {sortedStatusCounts.map((data, index) => (
            <div key={index} className="flex-1  items-center inline-flex rounded-lg outline-1 outline-offset-[-1px] outline-gray-200">
              <div className={`mx-3 flex items-center justify-center content rounded-lg w-[36px] h-[36px]`} style={{ backgroundColor: data.icColor }}>
                <div className="flex items-center justify-center  w-[36px]">
                  <img src={data.icon} />
                </div>
              </div>
              <div className="p-3 relative my-3 mr-3 h-[50px] w-full inline-flex flex-col justify-start items-start">
                <div className="absolute top-0 left-0 font-normal w-full justify-start text-gray-500 text-xs">{data.status}</div>
                <div className="absolute bottom-0 left-0 w-full self-stretch justify-start text-gray-900 text-lg font-bold">{data.count}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const TableHeader = () => {
    return (
      <div className="flex w-full justify-between items-center mb-3">
        <div className="font-semibold text-[14px]" style={{ color: THEME.COLORS.TABLE_TEXT }}>
          {totalJobs} {totalJobs === 1 ? TOTAL_SKETCH : TOTAL_SKETCHES}
        </div>
        <div className="flex items-center gap-2">
          <Button
            className="!h-[35px] disabled:opacity-50 !rounded-md !px-2 !py-1 !space-x-1 !border !border-gray-300 !cursor-pointer !font-semibold !inline-flex !justify-end !items-center"
            style={{ color: THEME.COLORS.ORANGE_600 }}
            onClick={() => mutation.mutate()}
            disabled={totalJobs === 0 || visibleColumns.length === 0}
          >
            {EXPORT_CSV}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="mx-8 my-5">
      <Dashboard />
      <div className="relative">
        <ToolBox
          setShowUIConfigModal={setShowUIConfigModal}
          searchInputValue={search_text}
          setSearchInputValue={setSearchText}
          handleFilterData={handleFilterData}
        />
        <div className="my-3 bg-[#FAFAFA] rounded-md transition-all duration-300 pb-0.5 min-h-[40px] border border-gray-200">
          <div className={`transition-all ${isVisible ? "overflow-visible" : "overflow-hidden"} ${isVisible ? "max-h-[155px] mb-1" : "max-h-10"}`}>
            {isVisible && (
              <div>
                <Filter setOptions={setOptions} handleFilterData={handleFilterData} setSearchInputValue={setSearchText} />
              </div>
            )}
            <div className="absolute bottom-[-10px] left-1/2 transform -translate-x-1/2 z-1 flex flex-col items-center">
              {!isVisible && <span className="font-semibold mb-1">{TEXT_FILTERS}</span>}
              <Button
                onClick={() => setIsVisible(!isVisible)}
                className="!w-[24px] !h-[24px] !p-0 !rounded-full !bg-orange-500 !text-white !flex !items-center !justify-center !shadow-lg !hover:bg-orange-500 !transition-all"
              >
                {isVisible ? <ChevronUp size={20} color="white" /> : <ChevronDown size={20} color="white" />}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <TableHeader />
      <BaseTable
        columns={visibleColumns.length > 1 ? visibleColumns : []}
        rowKey="id"
        scroll={{ x: "max-content", y: 449 }}
        dataSource={siteSketchList}
        style={{ marginTop: 12 }}
        onLoadMore={handleLoadMore}
        onChange={handleCreateSorter}
        loading={getSketchData.isPending}
        pagination={false}
      />
      {showUIConfigModal && (
        <UIConfigModal
          columns={columns}
          selectedColumns={visibleColumns}
          onClose={() => setShowUIConfigModal(false)}
          onSave={handleSaveUIConfig}
          isShowUIConfigModal={showUIConfigModal}
        />
      )}
    </div>
  );
};
